import React, { useState, useEffect } from "react";
import { BulkUpload } from "../../../../Entities/BulkUpload";
import {
  Table,
  TableColumnsType,
  Tag,
  Typography,
  Button,
  Pagination,
  Spin,
  Modal,
  Alert,
} from "antd";
import { Job } from "../../../../Entities/Job";
import bulkUploadService from "../../../../Service/bulk.upload.service";
import { EyeOutlined, LoadingOutlined, WarningOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ActiveBulkUploadFilterMenu from "./ActiveBulkUploadFilterMenu";
import { FileUploadsJobFilters, PaginationType } from "../../../../Interface";
import { classifyError } from "../../../../Utils/errorClassifier";
import { ERROR_MESSAGES } from "../../../../Utils/errorMessages";

interface ActiveBulkUploadProps {
  bulkUpload: BulkUpload | null;
  jobPostId: string;
  updateBulkUploadsCount: (
    bulkUploadId: string,
    noOfFilesCompleted: number,
    noOfFilesFailed: number,
  ) => void;
}

const ActiveBulkUpload: React.FC<ActiveBulkUploadProps> = ({
  bulkUpload,
  jobPostId,
  updateBulkUploadsCount,
}) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState<FileUploadsJobFilters>({
    status: null,
    failureReason: null,
    sort: null,
  });
  const [errorDetailJob, setErrorDetailJob] = useState<Job | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const handlePaginationChange = (page: number, pageSize?: number) => {
    setPagination((prev) => ({
      ...prev,
      page,
      limit: pageSize || prev.limit,
    }));
  };

  const handleFilterChange = (
    key: keyof FileUploadsJobFilters,
    value: string | null,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleClearAll = () => {
    setFilters({
      status: null,
      failureReason: null,
      sort: null,
    });

    setPagination((prev) => ({
      ...prev,
      page: 1,
      limit: 10,
    }));
  };

  const handleViewClick = (applicationId: string | undefined) => {
    if (!applicationId) return;
    const currentUrl = window.location.pathname + window.location.search;
    const encodedReturnUrl = encodeURIComponent(currentUrl);
    navigate(
      `/application-details?applicationId=${applicationId}&jobId=${jobPostId}&returnUrl=${encodedReturnUrl}`,
    );
  };

  const getFailureReason = (failureReason: Job["failureReason"] | null) => {
    if (!failureReason) return "";
    switch (failureReason) {
      case "application.limit.reached":
        return "Application Limit Reached";
      case "application.already.exists":
        return "Application Already Exists";
      case "failed.to.extract.user.details":
        return "Failed to Extract User Details";
      case "invalid.file.format":
        return "Invalid File Format";
      case "unable.to.process.resume":
        return "Unable to Process Resume";
      default:
        return failureReason;
    }
  };

  const getErrorCategoryFromJob = (job: Job) => {
    if (!job.errorCode) return null;
    // Create a mock error object for classification
    const mockError = {
      response: {
        status: 422,
        data: { errorCode: job.errorCode },
      },
    };
    return classifyError(mockError);
  };

  const openErrorDetails = (job: Job) => {
    setErrorDetailJob(job);
    setShowErrorModal(true);
  };

  const getStatus = (status: Job["status"]) => {
    switch (status) {
      case "pending":
        return {
          color: "blue",
          label: "Pending",
        };
      case "completed":
        return {
          color: "green",
          label: "Completed",
        };
      case "failed":
        return {
          color: "red",
          label: "Failed",
        };
      default:
        return {
          color: "default",
          label: "",
        };
    }
  };

  const columns: TableColumnsType<Job> = [
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      minWidth: 150,
      render: (data) => {
        return (
          <Typography.Text
            ellipsis={{ tooltip: data }}
            style={{ maxWidth: "150px", cursor: "pointer" }}
          >
            {data}
          </Typography.Text>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      minWidth: 100,
      render: (data, record) => {
        if (record.status === "pending") {
          return <Spin indicator={<LoadingOutlined spin />} size="small" />;
        } else {
          return (
            <Tag color={getStatus(record.status).color}>
              {getStatus(record.status).label}
            </Tag>
          );
        }
      },
    },
    {
      title: "Failure Reason",
      dataIndex: "failureReason",
      key: "failureReason",
      minWidth: 200,
      render: (data, record: Job) => {
        if (!data) return <Typography.Text type="secondary"></Typography.Text>;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography.Text type="danger">
              {getFailureReason(data || null)}
            </Typography.Text>
            {record.errorCode && (
              <Button
                type="link"
                size="small"
                icon={<WarningOutlined />}
                onClick={() => openErrorDetails(record)}
                title="View error details"
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      minWidth: 150,
      render: (data, record) =>
        record?.jobApplicationId ? (
          <Button
            icon={<EyeOutlined />}
            type="link"
            onClick={() => handleViewClick(record.jobApplicationId)}
          />
        ) : (
          <Typography.Text type="secondary"></Typography.Text>
        ),
    },
  ];

  const fetchJobs = async (bulkUploadId: string,page:number | null , limit:number | null) => {
    
    const pageObj: PaginationType = {
      page: page || 1,
      limit: limit || 10,
      totalRecords: 0,
      totalPages: 0,
    }
    
    const response = await bulkUploadService.getAllBulkUploadJobs(
      jobPostId,
      bulkUploadId,
      filters,
      pageObj,
    );
    setJobs(response.data);
    setPagination((prev) => ({
      ...prev,
      totalRecords: response.total,
      totalPages: response.totalPages,
    }));
    // Only update card counts when no filters are applied; filtered response is a subset and would overwrite real totals
    const hasNoFilters = !filters.status && !filters.failureReason;
    if (hasNoFilters) {
      const totalNoOfFilesCompleted = response.data.filter(
        (job: Job) => job.status === "completed",
      ).length;
      const totalNoOfFilesFailed = response.data.filter(
        (job: Job) => job.status === "failed",
      ).length;
      updateBulkUploadsCount(
        bulkUploadId,
        totalNoOfFilesCompleted,
        totalNoOfFilesFailed,
      );
    }
  };

  useEffect(() => {
    if (bulkUpload) {
      fetchJobs(bulkUpload._id.toString(),null,null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkUpload, jobPostId, filters, pagination.page, pagination.limit]);

  useEffect(() => {
    if (!bulkUpload) return;

    const isAllJobFetched =
      bulkUpload.totalNoOfFiles ===
      bulkUpload.noOfFilesCompleted + bulkUpload.noOfFilesFailed;
    if (isAllJobFetched) return;

    const intervalId = setInterval(() => {
      // apply pagination to fetch 100 jobs at a time for polling
      fetchJobs(bulkUpload._id.toString(),1,100);
    }, 5000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkUpload, jobPostId]);

  const isBulkUploadProcessing =
    bulkUpload &&
    bulkUpload.totalNoOfFiles >
      (bulkUpload.noOfFilesCompleted ?? 0) + (bulkUpload.noOfFilesFailed ?? 0);

  return (
    <div className="active-bulk-upload">
      {bulkUpload ? (
        <section className="active-bulk-upload-container">
          <ActiveBulkUploadFilterMenu
            handleFilterChange={handleFilterChange}
            filters={filters}
            handleClearAll={handleClearAll}
          />
          <div className="active-bulk-upload-table">
            <Table
              columns={columns}
              dataSource={jobs}
              size="small"
              pagination={false}
              scroll={{ x: "max-content", y: 350 }}
              rowKey="_id"
              locale={{
                emptyText:
                  jobs.length === 0 && (filters.status || filters.failureReason)
                    ? "No jobs match your filters"
                    : isBulkUploadProcessing
                      ? "Processing please wait..."
                      : "No data",
              }}
            />
          </div>
          <div className="active-bulk-upload-pagination">
            <Pagination
              total={pagination.totalRecords}
              pageSize={pagination.limit}
              current={pagination.page}
              onChange={handlePaginationChange}
              showSizeChanger={false}
              showTotal={(total, range) =>
                total > 0
                  ? `${range[0]}-${range[1]} of ${total} jobs`
                  : "0 jobs"
              }
            />
          </div>
        </section>
      ) : (
        <div className="active-bulk-upload-empty">
          <img
            src="/icons/ai-stars.svg"
            alt="ai-icon"
            className="ai-image-icon"
          />
          <Typography.Title level={5} style={{ margin: 0 }}>
            Select a bulk upload to view jobs
          </Typography.Title>
        </div>
      )}

      {/* Error Details Modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <WarningOutlined style={{ color: "#ff4d4f" }} />
            <span>Upload Error Details</span>
          </div>
        }
        open={showErrorModal}
        onCancel={() => setShowErrorModal(false)}
        footer={null}
        width={600}
      >
        {errorDetailJob && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <Typography.Text strong>File Name:</Typography.Text>
              <Typography.Paragraph>{errorDetailJob.fileName}</Typography.Paragraph>
            </div>

            {errorDetailJob.errorCode && (() => {
              const errorCategory = getErrorCategoryFromJob(errorDetailJob);
              const errorMessages = ERROR_MESSAGES[errorCategory?.category || "unknown"];

              return (
                <>
                  <Alert
                    message={errorMessages.recruiterHeadline}
                    description={errorMessages.recruiterBody}
                    type="error"
                    showIcon
                    style={{ marginBottom: "12px" }}
                  />

                  {errorMessages.recruiterGuidance && errorMessages.recruiterGuidance.length > 0 && (
                    <div>
                      <Typography.Text strong style={{ display: "block", marginBottom: "8px" }}>
                        What to do:
                      </Typography.Text>
                      <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {errorMessages.recruiterGuidance.map((guideline: string, idx: number) => (
                          <li key={idx} style={{ marginBottom: "6px" }}>
                            <Typography.Text>{guideline}</Typography.Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div style={{ backgroundColor: "#fafafa", padding: "12px", borderRadius: "4px" }}>
                    <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                      <strong>Error Code:</strong> {errorDetailJob.errorCode}
                    </Typography.Text>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ActiveBulkUpload;
